'use client';

import { useEffect, useRef } from 'react';
import { UserSettings } from 'src/environmentVariables/EnvironmentVariables';
import Input from 'src/components/primitives/Input/Input';
import useIsMainnet from 'src/hooks/useIsMainnet';
import SemanticChip from 'src/components/Utils/SemanticChip';
import { useAllRpcLatencies } from 'src/hooks/useRpcLatency';
import PriorityFeeSettingsInputs from 'src/components/Settings/NetworkSettings/PriorityFeeSettingsInputs';
import useDevSwitchIsOn from 'src/hooks/useDevSwitchIsOn';
import { Updater } from 'use-immer';
import { getRpcOptions } from 'src/utils/rpc';
import { Settings } from 'src/components/Settings/SettingsForm';
import { CUSTOM_RPC_LABEL } from 'src/constants/constants';
import RadioGroup from 'src/components/primitives/RadioGroup/RadioGroup';
import { cj } from 'src/utils/classNames';
import NetworkSettingsDevInputs from 'src/components/Settings/NetworkSettings/NetworkSettingsDevInputs';

interface Props {
	localSettings: Settings;
	setLocalSettings: Updater<Settings>;
	toggleRpcDropdown: () => void;
	isRpcDropdownOpen: boolean;
}

const RpcLatencyIndicator = ({ latency }: { latency: number }) => (
	<SemanticChip
		className="mr-2"
		status={
			latency < 0
				? 'grey'
				: latency < 100
					? 'green'
					: latency < 250
						? 'amber'
						: 'red'
		}
	/>
);

const NetworkSettings = ({
	localSettings,
	setLocalSettings,
	toggleRpcDropdown,
	isRpcDropdownOpen,
}: Props) => {
	const isMainnet = useIsMainnet();
	const isDevMode = useDevSwitchIsOn();

	const rpcOptions = getRpcOptions(isMainnet ? 'mainnet-beta' : 'devnet');

	const { value: selectedValue } = localSettings.rpcEndpoint;

	const latencies = useAllRpcLatencies();

	const rpcDropdown = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const checkDropdownClick = (e: MouseEvent) => {
			if (rpcDropdown?.current) {
				const isDescendantOfDropdown = rpcDropdown.current.contains(
					e.target as Node
				);

				// If clicked on the dropdown then toggle dropdown display regardless
				if (isDescendantOfDropdown) {
					toggleRpcDropdown();
					return;
				}

				// If not a descendant of the dropdown but the dropdown is currently open then toggle it off
				if (isRpcDropdownOpen && !isDescendantOfDropdown) {
					toggleRpcDropdown();
					return;
				}
			}
		};
		window.addEventListener('click', checkDropdownClick);

		return () => {
			window.removeEventListener('click', checkDropdownClick);
		};
	}, [isRpcDropdownOpen, rpcDropdown.current]);

	const updateSettingsValue = (update: Partial<UserSettings>) => {
		setLocalSettings((state) => {
			Object.entries(update).forEach((entry) => {
				state[entry[0] as keyof typeof state] = entry[1] as never;
			});
		});
	};

	return (
		<div className="flex flex-col gap-y-2xl">
			<div className="flex flex-col gap-y-sm">
				<h3 className="typo-label-sm text-text-default">RPC Endpoint</h3>
				<div className="flex flex-col gap-y-md">
					<RadioGroup.Root
						className="flex flex-col gap-md sm:flex-row"
						value={selectedValue}
						onValueChange={(value) =>
							updateSettingsValue({
								rpcEndpoint: rpcOptions.find((rpc) => rpc.value === value),
							})
						}
					>
						{rpcOptions.map((rpc) => (
							<RadioGroup.Item
								id={rpc.value}
								size="md"
								variant="card"
								key={rpc.value}
								className="flex-1"
								label={
									<div className="flex w-full justify-between typo-label-xs">
										<span
											className={cj(
												'typo-label-xs transition-colors',
												selectedValue === rpc.value
													? 'text-text-default'
													: 'text-text-secondary'
											)}
										>
											{rpc.label}
										</span>

										<div className="flex items-center">
											<RpcLatencyIndicator
												latency={latencies[rpc.value]?.avg ?? -1}
											/>
											<span className="text-text-default">
												{latencies[rpc.value]?.avg == null ||
												latencies[rpc.value]?.avg === -1
													? 'N/A'
													: `${latencies[rpc.value]?.avg} ms`}
											</span>
										</div>
									</div>
								}
								value={rpc.value}
							/>
						))}
					</RadioGroup.Root>

					<Input
						id="custom-rpc-url"
						labelVariant="inset"
						label="Enter custom RPC URL"
						type="text"
						value={
							localSettings.rpcEndpoint.label === CUSTOM_RPC_LABEL
								? localSettings.rpcEndpoint.value
								: ''
						}
						onChange={(value) => {
							updateSettingsValue({
								rpcEndpoint: { label: CUSTOM_RPC_LABEL, value },
							});
						}}
					/>
				</div>
			</div>

			<PriorityFeeSettingsInputs
				localSettings={localSettings}
				setLocalSettings={setLocalSettings}
			/>

			{isDevMode && (
				<NetworkSettingsDevInputs
					localSettings={localSettings}
					updateSettingsValue={updateSettingsValue}
				/>
			)}
		</div>
	);
};

export default NetworkSettings;
